import { useState, useEffect } from 'react';
import { Search, ChevronDown, Eye, Edit, Trash2, Plus, Upload, X, ArrowLeft } from 'lucide-react';

const SOXControlLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [cycleFilter, setCycleFilter] = useState('All SOX Cycles');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [riskFilter, setRiskFilter] = useState('All Risk Levels');
  const [controls, setControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [activeTab, setActiveTab] = useState('Excel');
  const [selectedControl, setSelectedControl] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Sample controls data
  const sampleControls = [
    {
      id: 1,
      name: 'Credit Approval Control',
      description: 'Ensures proper credit approval before order processing to mitigate credit risk and prevent bad debt losses.',
      owner: 'Sales Manager',
      backupOwner: 'Sales Director',
      soxCycle: 'Order to Cash',
      process: 'Sales Order Processing',
      riskLevel: 'high',
      frequency: 'Per Transaction',
      status: 'active',
      lastTested: '2024-11-10',
      controlType: 'Preventive',
      automationLevel: 'Manual',
      riskMitigated: 'Credit risk, bad debt losses, unauthorized sales',
      population: 'All sales orders > $10,000',
      sampleSize: '25 items per quarter',
      testingProcedure: 'Review credit approval documentation for selected samples and verify appropriate authorization levels.',
      expectedEvidence: 'Credit application, credit score report, approval signature/email',
      isKeyControl: true
    },
    {
      id: 2,
      name: 'Order Accuracy Review',
      description: 'Validates order details against customer requirements',
      owner: 'Order Clerk',
      backupOwner: 'Order Manager',
      soxCycle: 'Order to Cash',
      process: 'Sales Order Processing',
      riskLevel: 'moderate',
      frequency: 'Daily',
      status: 'active',
      lastTested: '2024-11-08',
      controlType: 'Detective',
      automationLevel: 'Semi-Automated',
      riskMitigated: 'Order errors, customer dissatisfaction',
      population: 'All sales orders',
      sampleSize: '50 items per quarter',
      testingProcedure: 'Compare order details with customer requirements',
      expectedEvidence: 'Order forms, customer emails, approval logs',
      isKeyControl: false
    },
    {
      id: 3,
      name: 'Revenue Cut-off Testing',
      description: 'Ensures revenue is recorded in the correct period',
      owner: 'Accounting Manager',
      backupOwner: 'CFO',
      soxCycle: 'Order to Cash',
      process: 'Revenue Recognition',
      riskLevel: 'high',
      frequency: 'Monthly',
      status: 'active',
      lastTested: '2024-10-15',
      controlType: 'Detective',
      automationLevel: 'Manual',
      riskMitigated: 'Revenue misstatement, period-end errors',
      population: 'All revenue transactions near period end',
      sampleSize: '30 items per quarter',
      testingProcedure: 'Review shipping documents and invoices for proper cutoff',
      expectedEvidence: 'Shipping logs, invoices, journal entries',
      isKeyControl: true
    },
    {
      id: 4,
      name: 'Vendor Invoice Approval',
      description: 'Three-way match verification for vendor invoices',
      owner: 'AP Manager',
      backupOwner: 'Controller',
      soxCycle: 'Procure to Pay',
      process: 'Invoice Processing',
      riskLevel: 'high',
      frequency: 'Per Transaction',
      status: 'active',
      lastTested: '2024-11-05',
      controlType: 'Preventive',
      automationLevel: 'Automated',
      riskMitigated: 'Fraudulent payments, duplicate payments',
      population: 'All vendor invoices > $5,000',
      sampleSize: '40 items per quarter',
      testingProcedure: 'Verify three-way match between PO, receipt, and invoice',
      expectedEvidence: 'Purchase orders, receiving reports, invoices, approval logs',
      isKeyControl: true
    },
    {
      id: 5,
      name: 'Payroll Authorization',
      description: 'Manager approval required for all payroll changes',
      owner: 'HR Manager',
      backupOwner: 'HR Director',
      soxCycle: 'Hire to Retire',
      process: 'Payroll Processing',
      riskLevel: 'moderate',
      frequency: 'Per Pay Period',
      status: 'draft',
      lastTested: '2024-10-30',
      controlType: 'Preventive',
      automationLevel: 'Semi-Automated',
      riskMitigated: 'Unauthorized payroll changes, fraud',
      population: 'All payroll change requests',
      sampleSize: '20 items per quarter',
      testingProcedure: 'Review payroll change forms for proper authorization',
      expectedEvidence: 'Change request forms, approval emails, payroll registers',
      isKeyControl: false
    }
  ];

  useEffect(() => {
    setControls(sampleControls);
    setFilteredControls(sampleControls);
  }, []);

  useEffect(() => {
    let filtered = [...controls];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(control =>
        control.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        control.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        control.owner.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply cycle filter
    if (cycleFilter !== 'All SOX Cycles') {
      filtered = filtered.filter(control => control.soxCycle === cycleFilter);
    }

    // Apply status filter
    if (statusFilter !== 'All Statuses') {
      filtered = filtered.filter(control => control.status === statusFilter);
    }

    // Apply risk filter
    if (riskFilter !== 'All Risk Levels') {
      filtered = filtered.filter(control => {
        if (riskFilter === 'High') return control.riskLevel === 'high';
        if (riskFilter === 'Moderate') return control.riskLevel === 'moderate';
        if (riskFilter === 'Low') return control.riskLevel === 'low';
        return true;
      });
    }

    setFilteredControls(filtered);
  }, [searchQuery, cycleFilter, statusFilter, riskFilter, controls]);

  const getRiskBadgeColor = (level) => {
    switch (level) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'draft':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'inactive':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const ImportModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[600px] max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Import Controls</h2>
          <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex border-b mb-6">
            {['Excel', 'Library', 'Workiva', 'AuditBoard'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === tab
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'Excel' && (
            <div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">Upload an Excel spreadsheet with your controls</p>
                <p className="text-sm text-gray-500 mb-4">Supported formats: .xlsx, .xls, .csv</p>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Choose File
                </button>
              </div>
              
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Expected columns:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Control Name</li>
                  <li>• Description</li>
                  <li>• Owner</li>
                  <li>• SOX Cycle</li>
                  <li>• Process</li>
                  <li>• Risk Level</li>
                  <li>• Frequency</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const ControlDetailView = () => (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSelectedControl(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{selectedControl.name}</h1>
                <div className="flex items-center space-x-3 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskBadgeColor(selectedControl.riskLevel)}`}>
                    {selectedControl.riskLevel === 'high' ? 'High Risk' : selectedControl.riskLevel === 'moderate' ? 'Moderate Risk' : 'Low Risk'}
                  </span>
                  <span className="text-sm text-gray-600">{selectedControl.soxCycle}</span>
                  {selectedControl.isKeyControl && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      Key Control
                    </span>
                  )}
                </div>
              </div>
            </div>
            {!isEditMode ? (
              <button
                onClick={() => setIsEditMode(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit Control</span>
              </button>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setIsEditMode(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setIsEditMode(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b">
            <div className="flex">
              <button className="px-6 py-3 text-sm font-medium text-blue-600 border-b-2 border-blue-600">
                Control Details
              </button>
              <button className="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700">
                Audit Milestones
              </button>
              <button className="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700">
                Testing History
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-600">Control Name</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={selectedControl.name}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{selectedControl.name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-600">Description</label>
                    {isEditMode ? (
                      <textarea
                        value={selectedControl.description}
                        rows={3}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{selectedControl.description}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Owner</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={selectedControl.owner}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{selectedControl.owner}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Backup Owner</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={selectedControl.backupOwner}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="mt-1 text-gray-900">{selectedControl.backupOwner}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Risk Level</label>
                      {isEditMode ? (
                        <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="high">High</option>
                          <option value="moderate">Moderate</option>
                          <option value="low">Low</option>
                        </select>
                      ) : (
                        <p className="mt-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskBadgeColor(selectedControl.riskLevel)}`}>
                            {selectedControl.riskLevel === 'high' ? 'High' : selectedControl.riskLevel === 'moderate' ? 'Moderate' : 'Low'}
                          </span>
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Frequency</label>
                      {isEditMode ? (
                        <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option>Per Transaction</option>
                          <option>Daily</option>
                          <option>Weekly</option>
                          <option>Monthly</option>
                          <option>Quarterly</option>
                          <option>Annually</option>
                        </select>
                      ) : (
                        <p className="mt-1 text-gray-900">{selectedControl.frequency}</p>
                      )}
                    </div>
                  </div>
                </div>

                <h3 className="text-lg font-semibold mt-8 mb-4">Risk & Control Attributes</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-600">Risk Mitigated</label>
                    {isEditMode ? (
                      <textarea
                        value={selectedControl.riskMitigated}
                        rows={2}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{selectedControl.riskMitigated}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Control Type</label>
                      <p className="mt-1 text-gray-900">{selectedControl.controlType}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Automation Level</label>
                      <p className="mt-1 text-gray-900">{selectedControl.automationLevel}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">SOX Cycle</label>
                      <p className="mt-1 text-gray-900">{selectedControl.soxCycle}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-600">Process</label>
                    <p className="mt-1 text-gray-900">{selectedControl.process}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Testing Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-600">Population</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={selectedControl.population}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{selectedControl.population}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-gray-600">Sample Size</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={selectedControl.sampleSize}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{selectedControl.sampleSize}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-gray-600">Testing Procedure</label>
                    {isEditMode ? (
                      <textarea
                        value={selectedControl.testingProcedure}
                        rows={3}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{selectedControl.testingProcedure}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-gray-600">Expected Evidence</label>
                    {isEditMode ? (
                      <textarea
                        value={selectedControl.expectedEvidence}
                        rows={2}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900">{selectedControl.expectedEvidence}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (selectedControl) {
    return <ControlDetailView />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Controls Library</h1>
            <p className="text-gray-600">Manage and maintain your library of SOX controls</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <Upload className="w-4 h-4" />
              <span>Import Controls</span>
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Add Control</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search controls..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="relative">
            <select
              value={cycleFilter}
              onChange={(e) => setCycleFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
            >
              <option>All SOX Cycles</option>
              <option>Order to Cash</option>
              <option>Procure to Pay</option>
              <option>Hire to Retire</option>
              <option>Record to Report</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
            >
              <option>All Statuses</option>
              <option>active</option>
              <option>draft</option>
              <option>inactive</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
            >
              <option>All Risk Levels</option>
              <option>High</option>
              <option>Moderate</option>
              <option>Low</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>
        </div>

        {/* Controls count and export */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-700 font-medium">
            Controls ({filteredControls.length})
          </p>
          <button className="text-gray-600 hover:text-gray-900 flex items-center space-x-2">
            <Upload className="w-4 h-4 rotate-180" />
            <span>Export</span>
          </button>
        </div>

        {/* Controls Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-slideUp animation-delay-100">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Control Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SOX Cycle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Tested
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredControls.map((control, index) => (
                <tr key={control.id} className="hover:bg-gray-50 transition-colors" style={{animationDelay: `${(index + 1) * 100}ms`}}>
                  <td className="px-6 py-4">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{control.name}</p>
                      <p className="text-sm text-gray-500 truncate max-w-xs">{control.description}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{control.owner}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{control.soxCycle}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskBadgeColor(control.riskLevel)}`}>
                      {control.riskLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{control.frequency}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(control.status)}`}>
                      {control.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{control.lastTested}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedControl(control)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-gray-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showImportModal && <ImportModal />}
    </div>
  );
};

export default SOXControlLibrary;